import React, { useState, useEffect } from 'react';
import {
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import axios from 'axios';
import toast from 'react-hot-toast';

const FeatureManagement = ({ tenantId, tenantName }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // Feature definitions with descriptions and dependencies
  const featureDefinitions = {
    inventory: {
      name: 'Inventory Management',
      description: 'Complete inventory tracking with items, product types, and history',
      icon: '📦',
      category: 'Core',
      subFeatures: ['items', 'types', 'services', 'history'],
      dependencies: []
    },
    reservations: {
      name: 'Reservation System',
      description: 'Booking and reservation management for items and services',
      icon: '📅',
      category: 'Core',
      subFeatures: ['items', 'services', 'processing', 'reports'],
      dependencies: ['inventory']
    },
    employees: {
      name: 'Employee Management',
      description: 'Employee profiles, documents, timesheets, and roles',
      icon: '👥',
      category: 'HR',
      subFeatures: ['profiles', 'documents', 'timesheets', 'roles'],
      dependencies: []
    },
    notifications: {
      name: 'Email Notifications',
      description: 'Automated email notifications for various events',
      icon: '📧',
      category: 'Communication',
      subFeatures: [],
      dependencies: []
    },
    scheduling: {
      name: 'Scheduling System',
      description: 'Advanced scheduling and calendar management',
      icon: '🗓️',
      category: 'Operations',
      subFeatures: [],
      dependencies: ['employees']
    },
    settings: {
      name: 'Organization Settings',
      description: 'Customizable organization-specific settings',
      icon: '⚙️',
      category: 'Core',
      subFeatures: [],
      dependencies: []
    },
    reports: {
      name: 'Reports & Analytics',
      description: 'Business intelligence and reporting tools',
      icon: '📊',
      category: 'Analytics',
      subFeatures: [],
      dependencies: ['reservations', 'inventory']
    },
    payments: {
      name: 'Payment Processing',
      description: 'Handle payments, deposits, and refunds',
      icon: '💳',
      category: 'Finance',
      subFeatures: [],
      dependencies: ['reservations']
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [tenantId]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      // Get organization's current features
      const response = await axios.get(`/api/platform/organizations/${tenantId}/modules`);
      
      // Merge with feature definitions
      const mergedFeatures = Object.keys(featureDefinitions).map(key => {
        const existingFeature = response.data.modules.find(m => m.name === key);
        return {
          id: key,
          ...featureDefinitions[key],
          enabled: existingFeature?.enabled || false,
          configuration: existingFeature?.configuration || {}
        };
      });
      
      setFeatures(mergedFeatures);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (featureId, enabled) => {
    setSaving({ ...saving, [featureId]: true });
    
    try {
      const feature = features.find(f => f.id === featureId);
      
      // Check dependencies when enabling
      if (enabled && feature.dependencies.length > 0) {
        const missingDeps = feature.dependencies.filter(
          dep => !features.find(f => f.id === dep)?.enabled
        );
        
        if (missingDeps.length > 0) {
          toast.error(`Please enable required features first: ${missingDeps.join(', ')}`);
          setSaving({ ...saving, [featureId]: false });
          return;
        }
      }
      
      // Check if any features depend on this when disabling
      if (!enabled) {
        const dependentFeatures = features.filter(
          f => f.dependencies.includes(featureId) && f.enabled
        );
        
        if (dependentFeatures.length > 0) {
          toast.error(`Cannot disable. Required by: ${dependentFeatures.map(f => f.name).join(', ')}`);
          setSaving({ ...saving, [featureId]: false });
          return;
        }
      }
      
      // Make API call to toggle feature
      const endpoint = enabled 
        ? `/api/platform/organizations/${tenantId}/modules/${featureId}/enable`
        : `/api/platform/organizations/${tenantId}/modules/${featureId}/disable`;
      
      await axios.post(endpoint);
      
      // Update local state
      setFeatures(features.map(f => 
        f.id === featureId ? { ...f, enabled } : f
      ));
      
      toast.success(`${feature.name} ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error(`Failed to ${enabled ? 'enable' : 'disable'} feature`);
    } finally {
      setSaving({ ...saving, [featureId]: false });
    }
  };

  // Group features by category
  const featuresByCategory = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading features...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Feature Management</h3>
          <p className="text-sm text-gray-500 mt-1">
            Enable or disable features for {tenantName}
          </p>
        </div>
        <button
          onClick={fetchFeatures}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
          <div className="ml-3 text-sm">
            <p className="text-yellow-800">
              <strong>Important:</strong> Disabling features may affect existing data and user access.
              Some features have dependencies that must be enabled first.
            </p>
          </div>
        </div>
      </div>

      {/* Features by Category */}
      <div className="space-y-6">
        {Object.entries(featuresByCategory).map(([category, categoryFeatures]) => (
          <div key={category} className="bg-white border rounded-lg">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h4 className="text-sm font-medium text-gray-900">{category} Features</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {categoryFeatures.map((feature) => (
                <div key={feature.id} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{feature.icon}</span>
                        <div>
                          <h5 className="text-sm font-medium text-gray-900">
                            {feature.name}
                          </h5>
                          <p className="text-sm text-gray-500 mt-1">
                            {feature.description}
                          </p>
                          
                          {/* Dependencies */}
                          {feature.dependencies.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-400">
                                Requires: {feature.dependencies.map(dep => 
                                  featureDefinitions[dep]?.name || dep
                                ).join(', ')}
                              </span>
                            </div>
                          )}
                          
                          {/* Sub-features */}
                          {feature.subFeatures.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-400">
                                Includes: {feature.subFeatures.join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex items-center">
                      {feature.enabled ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      
                      <Switch
                        checked={feature.enabled}
                        onChange={(checked) => toggleFeature(feature.id, checked)}
                        disabled={saving[feature.id]}
                        className={`${
                          feature.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                          saving[feature.id] ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <span
                          className={`${
                            feature.enabled ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <strong>{features.filter(f => f.enabled).length}</strong> of{' '}
            <strong>{features.length}</strong> features enabled
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
              Enabled
            </span>
            <span className="flex items-center">
              <XCircleIcon className="h-4 w-4 text-gray-400 mr-1" />
              Disabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureManagement;